import { useEffect, useMemo, useReducer } from 'react';
import apiClient from '../api/client';
import { ASSESSMENT_ENDPOINTS } from '../api/endpoints';
import { AssessmentAutosave, effectiveProspectEmail } from '../utils/assessmentAutosave';
import type { Quiz, ResponseValue } from '../types';

export function useAssessmentAutosave(
  quizId: string,
  quiz: Quiz | null,
  responses: Record<number, ResponseValue>,
  inheritedEmail: string | null | undefined,
): AssessmentAutosave {
  const [, render] = useReducer((value: number) => value + 1, 0);
  const controller = useMemo(() => new AssessmentAutosave({
    session: async (prospectEmail) => {
      const response = await apiClient.post<{ token: string; expiresAt: string }>(
        ASSESSMENT_ENDPOINTS.SESSION, { quizId, prospectEmail },
        { headers: { 'X-Skip-Auth': 'true' } },
      );
      return response.data;
    },
    draft: (snapshot, attempt) => apiClient.post(
      ASSESSMENT_ENDPOINTS.DRAFT,
      { quizId, ...snapshot, attemptId: attempt.attemptId },
      { headers: { 'X-Session-Token': attempt.token!, 'X-Skip-Auth': 'true' } },
    ),
  }), [quizId]);

  useEffect(() => {
    controller.start(render);
    return () => controller.stop();
  }, [controller]);

  useEffect(() => {
    // Do not carry the previous quiz's answers across a route change.
    if (!quiz || String(quiz.id) !== quizId) return;
    controller.update({ prospectEmail: effectiveProspectEmail(quiz, responses, inheritedEmail), responses });
  }, [controller, quizId, quiz, responses, inheritedEmail]);

  return controller;
}